import React, { useState, useEffect, useCallback, useMemo } from 'react';
import APIService from '../../services/api';
import { confirmErpia } from '../ConfirmDialog';

/**
 * Informations du collaborateur, avant l'envoi du contrat.
 *
 * Un CDI porte des informations que l'agence n'a pas : adresse, date de
 * naissance, n° AVS, IBAN de versement. Deux façons de les obtenir, au choix :
 * les demander au collaborateur par un formulaire en ligne, ou les saisir
 * soi-même. Aucune n'est obligatoire — le contrat part sans, comme avant, et
 * ces champs se remplissent alors à la main sur le document.
 *
 * Les CHAMPS ne sont pas écrits ici : ils viennent du formulaire composé dans
 * le module Forms. On choisit lequel envoyer ; ses champs à destination
 * connue deviennent la grille de saisie manuelle. Sans aucun formulaire
 * composé, une liste de secours prend le relais pour que le contrat reste
 * préparable.
 *
 * Routes :
 *   GET  /forms → les formulaires composés
 *   GET  /crm/flux/:id/formulaire-employe → { statut, envoye_le, recu_le, donnees, form_id? }
 *   POST /crm/flux/:id/formulaire-employe → { message, url, email_envoye, email_erreur? }
 *        Un second appel renvoie un NOUVEAU lien et invalide le précédent.
 */

const BASE = 'https://besoins-tat-production.up.railway.app';
const BI = ({ icon }) => <i className={`bi bi-${icon}`} aria-hidden="true" />;
const authH = () => ({ Authorization: `Bearer ${APIService.getToken()}` });
const jsonH = () => ({ ...authH(), 'Content-Type': 'application/json' });

/* Liste de SECOURS. Les champs envoyés viennent du formulaire choisi dans le
   module Forms — c'est lui qui décide de ce qu'on demande. Cette liste ne sert
   que si aucun formulaire n'est composé : le contrat doit rester préparable un
   jour où le module est vide. */
export const CHAMPS_COLLABORATEUR = [
  { cle: 'adresse', label: 'Adresse', large: true, placeholder: 'Rue et numéro' },
  { cle: 'npa', label: 'NPA', placeholder: '1204' },
  { cle: 'ville', label: 'Ville', placeholder: 'Genève' },
  { cle: 'date_naissance', label: 'Date de naissance', type: 'date' },
  { cle: 'nationalite', label: 'Nationalité', placeholder: 'Suisse' },
  { cle: 'permis', label: 'Permis de séjour', placeholder: 'B, C, G… ou aucun' },
  { cle: 'avs', label: 'N° AVS', mono: true, placeholder: '756.0000.0000.00' },
  { cle: 'etat_civil', label: 'État civil', placeholder: 'Célibataire, marié·e…' },
  { cle: 'iban', label: 'IBAN de versement', mono: true, large: true, placeholder: 'CH00 0000 0000 0000 0000 0' },
];

const fmtDate = (v) => {
  const d = v ? new Date(v) : null;
  return d && !Number.isNaN(d.getTime()) ? d.toLocaleDateString('fr-CH', { day: 'numeric', month: 'long' }) : null;
};

export default function PlacementInfosCollaborateur({ fluxId, valeurs, onChange, showNotification, nomCandidat, champErreur, onEffacerErreur }) {
  const [mode, setMode] = useState(null);          // null | 'demander' | 'saisir'
  const [etat, setEtat] = useState(null);          // { statut, envoye_le, recu_le } | null
  const [busy, setBusy] = useState(false);
  const [ouvert, setOuvert] = useState(false);
  // Le serveur renvoie l'URL du formulaire. Quand l'email n'est pas parti,
  // c'est le seul moyen d'atteindre la personne : on l'affiche et on la copie.
  const [lien, setLien] = useState(null);   // { url, email_envoye, motif }
  const [copie, setCopie] = useState(false);
  // Les formulaires composés dans le module Forms. C'est eux qui décident des
  // champs — le placement ne fait que choisir lequel part.
  const [forms, setForms] = useState(null);        // null = pas encore lu
  const [formId, setFormId] = useState('');

  useEffect(() => {
    let annule = false;
    fetch(`${BASE}/forms`, { headers: authH() })
      .then(r => (r.ok ? r.json() : null))
      .then(j => {
        if (annule || !j) return;
        const arr = j.data || j.forms || (Array.isArray(j) ? j : []);
        setForms(Array.isArray(arr) ? arr : []);
      })
      .catch(() => { if (!annule) setForms([]); });
    return () => { annule = true; };
  }, []);

  const charger = useCallback(async () => {
    if (!fluxId) return;
    try {
      const r = await fetch(`${BASE}/crm/flux/${encodeURIComponent(fluxId)}/formulaire-employe`, { headers: authH() });
      const j = await r.json().catch(() => ({}));
      if (!r.ok || j.success === false) return;
      const d = j.data ?? j;
      setEtat({ statut: d.statut || 'non_envoye', envoye_le: d.envoye_le, recu_le: d.recu_le });
      // Le formulaire réellement envoyé fait foi sur celui qu'on affiche.
      if (d.form_id != null) setFormId(String(d.form_id));
      // Des réponses reçues remplissent les champs — elles restent corrigeables.
      if (d.donnees && typeof d.donnees === 'object') {
        // Les réponses arrivent par clé de destination : on les pose telles
        // quelles, sans les filtrer sur une liste que le front connaîtrait.
        onChange(prev => ({ ...prev, ...d.donnees }));
        if (d.statut === 'recu') { setMode('saisir'); setOuvert(true); }
      }
    } catch { /* le contrat part sans ces informations, comme avant */ }
  }, [fluxId, onChange]);
  useEffect(() => { charger(); }, [charger]);

  const envoyer = async () => {
    if (etat?.statut === 'envoye') {
      const ok = await confirmErpia({
        title: 'Renvoyer le formulaire',
        message: 'Un nouveau lien part ; le précédent cessera de fonctionner.',
        detail: 'Si la personne a déjà ouvert l\'ancien lien sans le valider, sa saisie sera perdue.',
        confirmLabel: 'Renvoyer le formulaire', tone: 'danger',
      });
      if (!ok) return;
    }
    setBusy(true);
    try {
      const r = await fetch(`${BASE}/crm/flux/${encodeURIComponent(fluxId)}/formulaire-employe`, { method: 'POST', headers: jsonH(), body: JSON.stringify(formId ? { form_id: Number(formId) || formId } : {}) });
      const j = await r.json().catch(() => ({}));
      if (!r.ok || j.success === false) throw new Error(j.error || j.message || `HTTP ${r.status}`);
      const url = j.url || j.data?.url || '';
      setLien({ url, email_envoye: j.email_envoye !== false, motif: j.email_erreur || '' });
      setCopie(false);
      if (j.email_envoye === false) {
        showNotification?.(j.email_erreur || 'Le formulaire est prêt mais l\'email n\'est pas parti — transmettez le lien vous-même.', 'error');
      } else {
        showNotification?.(j.message || `Formulaire envoyé${nomCandidat ? ` à ${nomCandidat}` : ''}.`, 'success');
      }
      setEtat({ statut: 'envoye', envoye_le: new Date().toISOString() });
    } catch (e) { showNotification?.(e.message, 'error'); }
    finally { setBusy(false); }
  };

  // Un refus serveur ouvre le bloc : le champ fautif doit être visible.
  useEffect(() => { if (champErreur?.champ) { setOuvert(true); setMode('saisir'); } }, [champErreur]);

  // Le formulaire retenu : celui que le serveur dit avoir envoyé, sinon celui
  // marqué par défaut, sinon le premier composé.
  const formChoisi = useMemo(() => {
    const liste = Array.isArray(forms) ? forms : [];
    if (!liste.length) return null;
    return liste.find(f => String(f.id) === String(formId))
      || liste.find(f => f.defaut || f.usage === 'employe')
      || liste[0];
  }, [forms, formId]);
  useEffect(() => { if (!formId && formChoisi?.id != null) setFormId(String(formChoisi.id)); }, [formChoisi, formId]);

  // La grille de saisie ne montre que les champs à DESTINATION connue : ce
  // sont les seuls qui peuvent remplir le contrat et la fiche. Les autres ne
  // vivent que dans les réponses de la personne, et le bloc le dit.
  const champsForm = useMemo(() => (formChoisi?.champs || []).filter(c => c && c.destination), [formChoisi]);
  const sansDestination = (formChoisi?.champs || []).length - champsForm.length;
  const champs = champsForm.length
    ? champsForm.map(c => ({
      cle: c.destination,
      label: c.libelle || c.destination,
      type: c.type === 'date' ? 'date' : c.type === 'nombre' ? 'number' : c.type === 'email' ? 'email' : c.type === 'tel' ? 'tel' : 'text',
      mono: /avs|iban|npa/i.test(c.destination),
      large: /adresse|iban/i.test(c.destination),
      placeholder: '',
    }))
    // Aucun formulaire composé : la liste de secours, pour que le contrat
    // reste préparable.
    : CHAMPS_COLLABORATEUR;

  const remplis = champs.filter(c => String(valeurs?.[c.cle] || '').trim()).length;
  const statut = etat?.statut || 'non_envoye';
  const resume = statut === 'recu' ? `Réponses reçues${fmtDate(etat.recu_le) ? ` le ${fmtDate(etat.recu_le)}` : ''}`
    : statut === 'envoye' ? `Formulaire envoyé${fmtDate(etat.envoye_le) ? ` le ${fmtDate(etat.envoye_le)}` : ''} — en attente`
      : remplis ? `${remplis} information${remplis > 1 ? 's' : ''} saisie${remplis > 1 ? 's' : ''}`
        : 'Aucune information — le contrat partira sans';

  return (
    <div className="crm-infos-collab">
      <button type="button" className="crm-infos-collab-tete" onClick={() => setOuvert(o => !o)} aria-expanded={ouvert}>
        <BI icon={ouvert ? 'chevron-down' : 'chevron-right'} />
        <span className="crm-infos-collab-titre">Informations du collaborateur</span>
        <span className="crm-infos-collab-etat" data-ton={statut === 'recu' ? 'ok' : statut === 'envoye' ? 'attente' : remplis ? 'ok' : 'muet'}>{resume}</span>
      </button>

      {ouvert && (
        <div className="crm-infos-collab-corps">
          <p className="crm-infos-collab-aide">
            Adresse, date de naissance, n° AVS, IBAN : le contrat les reprend s'ils sont là.
            <strong> Rien n'est obligatoire</strong> — sans elles, le contrat part quand même et les champs se remplissent à la main.
          </p>

          {Array.isArray(forms) && forms.length > 0 && (
            <label className="crm-infos-collab-form">
              <span>Formulaire</span>
              <select value={formId} onChange={e => setFormId(e.target.value)} disabled={statut !== 'non_envoye'}>
                {forms.map(f => <option key={f.id} value={f.id}>{f.nom}</option>)}
              </select>
              <small>
                {statut !== 'non_envoye'
                  ? 'Déjà envoyé — pour en changer, renvoyez le formulaire.'
                  : `Composé dans Forms · ${(formChoisi?.champs || []).length} champ${(formChoisi?.champs || []).length > 1 ? 's' : ''}${sansDestination > 0 ? `, dont ${sansDestination} hors fiche` : ''}`}
              </small>
            </label>
          )}
          {Array.isArray(forms) && forms.length === 0 && (
            <p className="crm-infos-collab-aide">
              Aucun formulaire composé — la liste par défaut sera utilisée. Composez-en un dans <strong>Forms</strong> pour choisir ce qui est demandé.
            </p>
          )}

          <div className="crm-infos-collab-modes">
              <button type="button" className={mode === 'demander' ? 'is-active' : ''} onClick={() => setMode('demander')}>
                <BI icon="send" /> Demander au collaborateur
              </button>
              <button type="button" className={mode === 'saisir' ? 'is-active' : ''} onClick={() => setMode('saisir')}>
                <BI icon="pencil" /> Saisir maintenant
              </button>
          </div>

          {mode === 'demander' && (
            <div className="crm-infos-collab-envoi">
              <span>
                {statut === 'envoye'
                  ? 'Le formulaire est parti. Ses réponses rempliront ces champs dès qu\'il sera complété — vous pouvez continuer sans attendre.'
                  : `Un lien part ${nomCandidat ? `à ${nomCandidat}` : 'au collaborateur'} ; ses réponses reviennent ici.`}
              </span>
              <button type="button" className="crm-placement-secondary" onClick={envoyer} disabled={busy}>
                {busy ? 'Envoi…' : statut === 'envoye' ? 'Renvoyer le formulaire' : 'Envoyer le formulaire'}
              </button>
            </div>
          )}

          {lien && (
            <div className="crm-infos-collab-lien" data-ton={lien.email_envoye ? 'ok' : 'alerte'} role="status">
              <BI icon={lien.email_envoye ? 'link-45deg' : 'exclamation-triangle-fill'} />
              <div>
                <strong>{lien.email_envoye ? 'Lien du formulaire' : 'Lien prêt — l\'email n\'est pas parti'}</strong>
                {!lien.email_envoye && <span className="crm-infos-collab-lien-motif">{lien.motif || 'Transmettez ce lien vous-même.'}</span>}
                <code className="erp-mono">{lien.url || '—'}</code>
              </div>
              {lien.url && (
                <button type="button" className="crm-placement-secondary"
                  onClick={async () => { try { await navigator.clipboard.writeText(lien.url); setCopie(true); } catch { showNotification?.('Copie impossible — sélectionnez le lien à la main.', 'error'); } }}>
                  {copie ? 'Copié' : 'Copier le lien'}
                </button>
              )}
            </div>
          )}

          {(mode === 'saisir' || statut === 'recu') && (
            <div className="crm-infos-collab-grille">
              {champs.map(c => (
                <label key={c.cle} className={c.large ? 'is-large' : undefined}>
                  {c.label}
                  <input type={c.type || 'text'} className={c.mono ? 'erp-mono' : undefined}
                    value={valeurs?.[c.cle] || ''} placeholder={c.placeholder || ''}
                    aria-invalid={champErreur?.champ === c.cle || undefined}
                    style={champErreur?.champ === c.cle ? { borderColor: 'var(--danger-bd)' } : undefined}
                    onChange={e => { const v = e.target.value; onChange(prev => ({ ...prev, [c.cle]: v })); if (champErreur?.champ === c.cle) onEffacerErreur?.(); }} />
                  {champErreur?.champ === c.cle && (
                    <span role="alert" style={{ fontSize: '10.5px', lineHeight: 1.4, color: 'var(--danger-fg)' }}>{champErreur.message}</span>
                  )}
                </label>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
